import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as HttpStatus from 'http-status-codes';
import { PayPalEcStatesEnum } from 'i2g-ng-payment';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { List, Map } from 'immutable';
import { get, isBoolean, noop } from 'lodash';
import { Observable, Subject } from 'rxjs';
import {
  delay,
  filter,
  finalize,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs/operators';
import { v4 } from 'uuid';
import { SettingsDataStore } from '../../..//settings-data/settings-data.store';
import { Account } from '../../../account-data/account.model';
import { BrowserManagerDataService } from '../../../browser-manager-data/browser-manager-data.service';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import {
  DialogActionsEnum,
  IDialogData,
  IDialogTrigger,
} from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import {
  DOCUMENTS_NEW_ROUTE,
  DocumentTypesEnum,
} from '../../../document-data/document-data.data';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { DepositPayment } from '../../../document-data/sub-models/deposit-payment.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { NavigationService } from '../../../navigation/navigation.service';
import { PaymentsDataStateService } from '../../../payments-data/payments-data-state.service';
import {
  getDefaultReminders,
  IInvalidEmailConfirmDialogData,
  PaymentErrorsEnum,
} from '../../../payments-data/payments-data.data';
import { PaymentsDataDispatcher } from '../../../payments-data/payments-data.dispatcher';
import { UpdatePaymentMethodParams } from '../../../payments/components/payment-options/payment-options.interface';
// eslint-disable-next-line max-len
import { DIALOG_TYPE as setupBankTransferDialog } from '../../../payments/components/setup-bank-transfer-dialog/setup-bank-transfer-dialog.component';
import { I2gMoneyContext } from '../../../settings-data/i2g-money.data';
import { SettingsDataDispatcher } from '../../../settings-data/settings-data.dispatcher';
import { Settings } from '../../../settings-data/settings.model';
import { PaymentReminders } from '../../../settings-data/sub-models/company-settings/payment-reminders.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { PaymentRemindersBuilder } from '../../../settings/components/payment-reminders/payment-reminders.builder';
import { GlobalBannerTypesEnum } from '../../../shared/components/global-banner/global-banner.enum';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { MessageToastComponent } from '../../../shared/components/message-toast/message-toast.component';
import { EmailService } from '../../../shared/services/email/email.service';
import { UrlService } from '../../../shared/services/url/url.service';
import { IToasterConfig, IToasterData } from '../../../toaster/toaster.data';
import { ToasterDispatcher } from '../../../toaster/toaster.dispatcher';
import {
  IPaymentOptionsAdditionalProperties,
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DIALOG_TYPE as remindersDisabledDialog } from '../reminders-disabled-dialog/reminders-disabled-dialog.component';
import { DIALOG_TYPE as removeDepositRequestDialog } from '../remove-deposit-request-dialog/remove-deposit-request-dialog.component';
import { DIALOG_TYPE as viewRemindersDialog } from '../view-reminders-dialog/view-reminders-dialog.component';
import { DocumentPaymentOptionsBuilder } from './document-payment-options.builder';

@Component({
  selector: 'app-document-payment-options',
  templateUrl: './document-payment-options.component.html',
  styleUrls: ['./document-payment-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DocumentPaymentOptionsBuilder, PaymentRemindersBuilder],
})
export class DocumentPaymentOptionsComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() showI2gMoney: boolean;
  @Input() showBankTransfer: boolean;
  @Input() showCardPayments: boolean;
  @Input() showPaypalEc: boolean;
  @Input() i2gMoneyEnabled: boolean;
  @Input() bankTransferEnabled: boolean;
  @Input() cardPaymentsEnabled: boolean;
  @Input() paypalEcEnabled: boolean;
  @Input() paypalEcStatus: PayPalEcStatesEnum;
  @Input() documentId: string;
  @Input() documentType: DocumentTypesEnum;
  @Input() documentBilling: Billing;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() documentCalculationResult: CalculationResult;
  @Input() companyCountry: string;
  @Input() underwritingUrl: string;
  @Input() paymentsSettings: PaymentsSettings;
  @Input() remindersSettings: PaymentReminders;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input()
  paymentOptionsTrackingProperties: IPaymentOptionsAdditionalProperties;
  @Input() featureToggles: Toggles;
  @Input() account: Account;
  @Input() deposit: Deposit;
  @Input() showPaymentReminders: boolean;

  @Output() savePaymentOptions: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() growthCta: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _remindersSchedule: List<number>;
  private _changeCardPaymentsFeatureName: string;
  private _changePaypalFeatureName: string;
  private _paymentRemindersFeatureName: string;

  form: FormGroup;
  formKeys: any;
  invalidEmailDialogId: string = v4();
  featureHighlightIcon: IIcon;
  viewRemindersTrackingAction: ITrackingAction;

  i2gMoneyContext$: Observable<I2gMoneyContext>;

  constructor(
    private _documentPaymentOptionsBuilder: DocumentPaymentOptionsBuilder,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _paymentsDataDispatcher: PaymentsDataDispatcher,
    private _paymentDataStateService: PaymentsDataStateService,
    private _featuresDataStore: FeaturesDataStore,
    private _settingsDataStore: SettingsDataStore,
    private _toasterDispatcher: ToasterDispatcher,
    private _dialogService: DialogService,
    private _emailService: EmailService,
    private _navigationService: NavigationService,
    private _translateService: TranslateService,
    private _urlService: UrlService,
    private _globalBannerService: GlobalBannerService,
    private _browserManagerDataService: BrowserManagerDataService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.featureToggles ||
        changes.remindersSettings ||
        changes.documentSettings) &&
      this.featureToggles &&
      this.remindersSettings &&
      this.documentSettings &&
      !this.form
    ) {
      this.buildForm();
    }
  }

  ngOnInit() {
    this._changeCardPaymentsFeatureName = Toggles.KEYS.CHANGE_CARD_PAYMENTS;
    this._changePaypalFeatureName = Toggles.KEYS.CHANGE_PAYPAL_EC;
    this._paymentRemindersFeatureName = Toggles.KEYS.PAYMENT_REMINDERS;
    this._remindersSchedule = this.remindersSettings.remindIn.isEmpty()
      ? List(getDefaultReminders(this.featureToggles))
      : this.remindersSettings.remindIn;
    this.viewRemindersTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'view_payment_reminders',
      this.trackingScreenName
    );

    this._featuresDataStore
      .hasFeatureHighlightCanvas(this._paymentRemindersFeatureName)
      .pipe(
        takeUntil(this._destroyed),
        filter((x) => !!x)
      )
      .subscribe(() => {
        this.featureHighlightIcon = {
          name: 'premium-feature',
          size: IconSizesEnum.medium,
          customClass: 'premium-icon',
        };
      });

    this.i2gMoneyContext$ = this._settingsDataStore.getI2gMoney().pipe(
      filter((x) => !!x),
      map((value) => value.context),
      shareReplay(1)
    );
  }

  buildForm() {
    this.form = this._documentPaymentOptionsBuilder.buildForm(
      this.remindersSettings,
      this.documentSettings,
      this.featureToggles
    );
    this.formKeys = this._documentPaymentOptionsBuilder.FORM_KEYS;

    if (this.showPaymentReminders) {
      const remindersEnabledControl: AbstractControl = this.form.get(
        this.formKeys.REMINDERS_ENABLED
      );

      remindersEnabledControl.valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe((enabled: boolean) => {
          if (enabled) {
            // If user doesn't have privilege, show dialog
            if (
              this.featureToggles.isNoAccess(this._paymentRemindersFeatureName)
            ) {
              this._dialogService.openGrowthDialog(
                BannerCanvasTypesEnum.feature_lock,
                this._paymentRemindersFeatureName,
                this.growthCta.emit.bind(this.growthCta)
              );

              remindersEnabledControl.setValue(false, { emitEvent: false });
            } else if (
              !this.featureToggles.isAllAccess(
                this._paymentRemindersFeatureName
              )
            ) {
              this._dialogService.openDialog({ type: remindersDisabledDialog });

              remindersEnabledControl.setValue(false, { emitEvent: false });
            } else {
              // If billing email isn't valid, let user know
              const email = get(this.documentBilling, Billing.KEYS.EMAIL, '');

              if (!email || !this._emailService.validateEmails(email)) {
                this._dialogService.openConfirmDialog(
                  this.invalidEmailDialogId,
                  {
                    documentType: this.documentType,
                    documentId: this.documentId,
                  } as IInvalidEmailConfirmDialogData
                );
              }

              this._saveReminderOptions(true);

              // If user has privilege and reminders aren't already on, turn on account reminders
              this._enableAccountReminders();
            }
          } else {
            this._saveReminderOptions(false);
          }
        });
    }
  }

  onUpdateI2gMoney(updateParams: UpdatePaymentMethodParams) {
    const i2gMoneyEnabled = updateParams.enabled;
    const props = this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsForI2gMoney(
      i2gMoneyEnabled
    );

    // if enabling I2g Money
    if (i2gMoneyEnabled) {
      this.i2gMoneyContext$
        .pipe(takeUntil(this._destroyed))
        .subscribe((context: I2gMoneyContext) => {
          const { settings } = context;
          const isAccountLevelEnabled =
            settings.allowed_by_partner && settings.enabled_by_user;

          if (!isAccountLevelEnabled) {
            // if I2G Money is disabled at account level
            this._enableI2gMoney(props);
          } else {
            this.savePaymentOptions.emit(props); // if account settings is already enabled, just update documents
          }
        });
    } else {
      // if disabling I2g payments
      if (
        this._checkRemoveDepositRequestDialog(
          // if all payments are disabled and there's a deposit
          this.documentCalculationResult,
          this.cardPaymentsEnabled,
          i2gMoneyEnabled
        )
      ) {
        this._triggerRemoveDepositRequestDialog(props, () =>
          this._enableI2gMoney(
            this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsForI2gMoney(
              true
            )
          )
        ); // then trigger remove desposit dialog
      } else {
        if (updateParams.atAccountLevel) {
          this._disableI2gMoney(props);
        } else {
          this.savePaymentOptions.emit(props); // if not, just disable on document settings
        }
      }
    }
  }

  onUpdateBankTransfer(updateParams: UpdatePaymentMethodParams) {
    const bankTransferEnabled = updateParams.enabled;
    const props = this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsForBankTransfer(
      bankTransferEnabled
    );

    // if enabling bank transfer
    if (bankTransferEnabled) {
      if (!this.paymentsSettings.bankTransferEnabled()) {
        // if bank transfer on account settings is disabled
        if (this.paymentsSettings.achDebit.isUnderwritingRequired()) {
          // if underwriting is required
          this._dialogService.openDialog({ type: setupBankTransferDialog }); // open setup bank transfer canvas
        } else {
          this._enableBankTransfer(props); // try enable bank transfer
        }
      } else {
        this.savePaymentOptions.emit(props); // if it's already enabled, just update documents
      }
    } else {
      // if disabling bank transfer
      if (updateParams.atAccountLevel) {
        this._disableBankTransfer(props);
      } else {
        this.savePaymentOptions.emit(props); // just disable on document settings
      }
    }
  }

  onConfirmSetupBankTransferDialog() {
    this._dialogService.openLoadingDialog();

    this._browserManagerDataService.tabVisible$
      .pipe(
        filter((x) => !!x),
        delay(2000), // eventual consistency: adding delay so we get the latest payment settings set on the backend
        take(1),
        flatMap(() => this._settingsDataDispatcher.fetchSettings()),
        take(1),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe((settings: Settings) => {
        if (settings.payments.bankTransferEnabled()) {
          this.savePaymentOptions.emit(
            this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsForBankTransfer(
              true
            )
          );
        }
      });
  }

  onUpdateCardPayments(updateParams: UpdatePaymentMethodParams) {
    const cardPaymentsEnabled = updateParams.enabled;

    // This prepares the settings for the document only
    const props = this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsForCardPayments(
      cardPaymentsEnabled
    );

    // if enabling card payments
    if (cardPaymentsEnabled) {
      const isAccountLevelEnabled =
        this.paymentsSettings.cardPayments &&
        this.paymentsSettings.cardPayments.stripe &&
        this.paymentsSettings.cardPayments.stripe.isEnabled();

      if (!isAccountLevelEnabled) {
        // if card payments on account settings is disabled
        this._enableCardPayments(props);
      } else {
        this.savePaymentOptions.emit(props); // if account settings is already enabled, just update documents
      }
    } else {
      // if disabling card payments
      if (
        !this.featureToggles.isAllAccess(this._changeCardPaymentsFeatureName)
      ) {
        // if not allowed to disable card payments
        this._dialogService.openGrowthDialog(
          BannerCanvasTypesEnum.feature_lock,
          this._changeCardPaymentsFeatureName
        );
      } else if (
        this._checkRemoveDepositRequestDialog(
          // if all payments are disabled and there's a deposit
          this.documentCalculationResult,
          cardPaymentsEnabled,
          this.paypalEcEnabled
        )
      ) {
        this._triggerRemoveDepositRequestDialog(props, () =>
          this._enableCardPayments(
            this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsForCardPayments(
              true
            )
          )
        ); // then trigger remove desposit dialog
      } else {
        if (updateParams.atAccountLevel) {
          this._disableCardPayments(props);
        } else {
          this.savePaymentOptions.emit(props); // if not, just disable on document settings
        }
      }
    }
  }

  onUpdatePaypal(updateParams: UpdatePaymentMethodParams) {
    const paypalEcEnabled = updateParams.enabled;

    // Trigger onboarding canvas if user has not completed underwriting
    if (
      this._paymentDataStateService.isPayPalUnderwritingRequired(
        this.paymentsSettings.paypalEcStatus
      )
    ) {
      this._dialogService.openLoadingDialog();

      const currentHref = this._navigationService.getLocationHref();
      const currentUrl = this._urlService.parse(currentHref);
      const currentPath = currentUrl.path;
      const newPath = currentPath.replace(
        `/${DOCUMENTS_NEW_ROUTE}`,
        `/${this.documentId}`
      );
      const redirectUrl = `${currentUrl.protocol}://${currentUrl.host}${newPath}`;

      this.documentDataDispatcher
        .saveEditingEntity()
        .pipe(
          takeUntil(this._destroyed),
          finalize(() => this._dialogService.closeLoadingDialog())
        )
        .subscribe(() => {
          this._paymentsDataDispatcher.triggerPayPalOnboardingCanvas(
            redirectUrl
          );
        }, noop);

      return;
    }

    // This prepares props for document settings only
    const props = this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsForPaypal(
      paypalEcEnabled
    );

    // we only care currently about turning off
    // to enable, the payments must be enabled at account level
    if (paypalEcEnabled) {
      // enabling paypal
      // checking if already enabled at account level
      const isAccountLevelEnabled = this.paymentsSettings.paypalEcEnabled;

      if (!isAccountLevelEnabled) {
        // if paypal on account settings is disabled
        this._updatePayPal(props, true);
      } else {
        this.savePaymentOptions.emit(props); // if it's already enabled, just update documents
      }
    } else {
      if (!this.featureToggles.isAllAccess(this._changePaypalFeatureName)) {
        // if not allowed to disable paypal
        this._dialogService.openGrowthDialog(
          BannerCanvasTypesEnum.feature_lock,
          this._changePaypalFeatureName
        );
      } else if (
        this._checkRemoveDepositRequestDialog(
          // if all payments are disabled and there's a deposit
          this.documentCalculationResult,
          this.cardPaymentsEnabled,
          paypalEcEnabled
        )
      ) {
        this._triggerRemoveDepositRequestDialog(props, () =>
          this._updatePayPal(
            this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsForPaypal(
              true
            ),
            true
          )
        ); // then trigger remove desposit dialog
      } else {
        if (updateParams.atAccountLevel) {
          this._updatePayPal(props, false);
        } else {
          this.savePaymentOptions.emit(props); // if not, just disable on document settings
        }
      }
    }
  }

  onInvalidClientEmail({ result, data }: IConfirmDialogResult) {
    const { documentId }: IInvalidEmailConfirmDialogData = data;

    if (result && documentId) {
      this.documentDataDispatcher.goToDocumentAddClientScreen(documentId);
    }
  }

  openViewRemindersDialog() {
    if (this.showPaymentReminders) {
      this._dialogService.openDialog({
        type: viewRemindersDialog,
        data: {
          headerLabel: this._translateService.instant(
            'LABEL.VIEW_PAYMENT_REMINDERS'
          ),
          schedule: this._remindersSchedule,
        },
      });
    }
  }

  showFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      this._paymentRemindersFeatureName,
      this.growthCta.emit.bind(this.growthCta)
    );
  }

  sendRemindersPreview() {
    if (this.showPaymentReminders) {
      const translateParam = { emailAddress: this.account.email };
      const toastData: IToasterData = {
        label: this._translateService.instant(
          'SETTINGS.CLIENT_COMMUNICATION.PAYMENT_REMINDERS.PREVIEW_SENT',
          translateParam
        ),
      };
      const toastConfig: IToasterConfig = {
        type: 'reminders-preview',
        component: MessageToastComponent,
        data: toastData,
      };

      this._toasterDispatcher.showToast(toastConfig);

      this._paymentsDataDispatcher.sendRemindersPreview(
        this._remindersSchedule
      );
    }
  }

  private _enableI2gMoney(props: Map<string, any>) {
    this._dialogService.openLoadingDialog();

    this._settingsDataDispatcher
      .updateI2gMoneyContext(true)
      .pipe(
        take(1),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(() => this.savePaymentOptions.emit(props), noop);
  }

  private _enableBankTransfer(props: Map<string, any>) {
    this._dialogService.openLoadingDialog();

    this._settingsDataDispatcher
      .updateBankTransfer(true, this._navigationService.getLocationHref())
      .pipe(
        take(1),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(
        (settings: Settings) => {
          const achDebit = settings.payments.achDebit;

          if (achDebit.isUnderwritingRequired()) {
            // open setup bank transfer dialog if underwriting required
            this._dialogService.openDialog({ type: setupBankTransferDialog });
          } else if (achDebit.isEnabled()) {
            // update document settings after enabling bank transfer
            this.savePaymentOptions.emit(props);
          }
        },
        ([status, errorBody, error]) => {
          const handledPaymentErrors = [
            PaymentErrorsEnum.unsupported_country_or_currency,
          ];

          if (
            !errorBody ||
            status !== HttpStatus.BAD_REQUEST ||
            !handledPaymentErrors.includes(errorBody.code)
          ) {
            this._globalBannerService.showSystemErrorBanner(error);
            return;
          }

          switch (errorBody.code) {
            case PaymentErrorsEnum.unsupported_country_or_currency:
              this._globalBannerService.showBanner({
                type: GlobalBannerTypesEnum.error,
                message: this._translateService.instant(
                  'PAYMENTS.STRIPE.UNSUPPORTED_COUNTRY'
                ),
                dismissible: true,
              });
              break;
          }
        }
      );
  }

  private _disableBankTransfer(props: Map<string, any>) {
    this._dialogService.openLoadingDialog();

    this._settingsDataDispatcher
      .updateBankTransfer(false, this._navigationService.getLocationHref())
      .pipe(
        take(1),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(
        () => this.savePaymentOptions.emit(props),
        (error) => this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  private _enableCardPayments(props: Map<string, any>) {
    const currentHref = this._navigationService.getLocationHref();
    const currentUrl = this._urlService.parse(currentHref);
    const currentPath = currentUrl.path;
    const newPath = currentPath.replace(
      `/${DOCUMENTS_NEW_ROUTE}`,
      `/${this.documentId}`
    );
    const redirectUrl = `${currentUrl.protocol}://${currentUrl.host}${newPath}`;

    this._dialogService.openLoadingDialog();

    this.documentDataDispatcher
      .saveEditingEntity()
      .pipe(
        flatMap(() =>
          this._settingsDataDispatcher.updateCardPaymentsSettings(
            true,
            redirectUrl
          )
        ),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(
        () => this.savePaymentOptions.emit(props), // update on document settings
        noop
      );
  }

  private _disableCardPayments(props: Map<string, any>) {
    this._dialogService.openLoadingDialog();

    this.documentDataDispatcher
      .saveEditingEntity()
      .pipe(
        flatMap(() =>
          this._settingsDataDispatcher.updateCardPaymentsSettings(false, '')
        ),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(
        () => this.savePaymentOptions.emit(props), // update on document settings
        (error) => this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  private _updatePayPal(props: Map<string, any>, enabled: boolean) {
    this._dialogService.openLoadingDialog();

    this._settingsDataDispatcher
      .updatePaypalSettings(enabled)
      .pipe(
        take(1),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(
        () => this.savePaymentOptions.emit(props), // update document settings after enabling paypal
        (error) => this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  private _disableI2gMoney(props: Map<string, any>) {
    this._dialogService.openLoadingDialog();

    this._settingsDataDispatcher
      .updateI2gMoneyContext(false)
      .pipe(
        take(1),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(
        () => this.savePaymentOptions.emit(props),
        (error) => this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  private _checkRemoveDepositRequestDialog(
    documentCalculationResult: CalculationResult,
    cardPaymentsEnabled: boolean,
    paypalEcEnabled: boolean
  ): boolean {
    const depositPayment: DepositPayment =
      documentCalculationResult && documentCalculationResult.payments.deposit;
    const allPaymentMethodsAreDisabled =
      isBoolean(cardPaymentsEnabled) &&
      !cardPaymentsEnabled &&
      isBoolean(paypalEcEnabled) &&
      !paypalEcEnabled;

    return (
      depositPayment &&
      depositPayment.isUnpaid() &&
      allPaymentMethodsAreDisabled
    );
  }

  /**
   * Trigger confirmation for deposit request
   *
   * @param props Document settings props
   * @param keepPaymentsOnCallback Callback to turn on payments
   * @private
   */
  private _triggerRemoveDepositRequestDialog(
    props: Map<string, any>,
    keepPaymentsOnCallback: () => void
  ) {
    this._dialogService.openDialog({ type: removeDepositRequestDialog });

    this._dialogService.dialogTriggers$
      .pipe(
        filter(
          ({ type, action }: IDialogTrigger) =>
            type === removeDepositRequestDialog &&
            action === DialogActionsEnum.close
        ),
        take(1),
        takeUntil(this._destroyed)
      )
      .subscribe(({ data }: IDialogTrigger) => {
        const { result } = data as IDialogData;

        if (result) {
          // remove deposit request
          this.savePaymentOptions.emit(props);
        } else {
          // turn on payments
          keepPaymentsOnCallback();
        }
      });
  }

  private _enableAccountReminders() {
    if (this.showPaymentReminders && !this.remindersSettings.enabled) {
      const props = this._documentPaymentOptionsBuilder.buildSettingsPropsFromReminders(
        true,
        this._remindersSchedule
      );

      this._settingsDataDispatcher.updateSettings(props);
      this._settingsDataDispatcher.saveCurrentSettingsToNetwork();
    }
  }

  private _saveReminderOptions(remindersEnabled: boolean) {
    const props = this._documentPaymentOptionsBuilder.buildDocumentSettingsPropsFromReminders(
      remindersEnabled
    );

    this.savePaymentOptions.emit(props);
  }

  ngOnDestroy() {
    this._dialogService.closeLoadingDialog();
    this._dialogService.closeConfirmDialog(this.invalidEmailDialogId);
    this._dialogService.closeDialog({ type: removeDepositRequestDialog });
    this._dialogService.closeDialog({ type: viewRemindersDialog });
    this._dialogService.closeDialog({ type: remindersDisabledDialog });
    this._dialogService.closeDialog({ type: setupBankTransferDialog });
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      this._changeCardPaymentsFeatureName
    );
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      this._changePaypalFeatureName
    );
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      this._paymentRemindersFeatureName
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
